/**
 * Deterministic face preprocessing pipeline
 * MUST match server-side preprocessing exactly
 */

import { BBox } from '../types';

export interface PreprocessedFace {
  normalized: Float32Array; // normalized face image for model input
  aligned: boolean;
}

/**
 * Normalize bbox coordinates to [0,1] range
 */
export function normalizeBBox(bbox: BBox, imageWidth: number, imageHeight: number): BBox {
  return {
    x: bbox.x / imageWidth,
    y: bbox.y / imageHeight,
    w: bbox.w / imageWidth,
    h: bbox.h / imageHeight,
  };
}

/**
 * Crop and align face from image using bbox
 * Returns normalized tensor ready for ONNX model input
 */
export function preprocessFace(
  imageData: ImageData | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement,
  bbox: BBox,
  targetSize: [number, number] = [112, 112] // standard face recognition input size
): PreprocessedFace {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  let sourceWidth: number;
  let sourceHeight: number;
  let sourceImage: CanvasImageSource;

  if (imageData instanceof ImageData) {
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    ctx.putImageData(imageData, 0, 0);
    sourceWidth = imageData.width;
    sourceHeight = imageData.height;
    sourceImage = canvas;
  } else {
    sourceWidth =
      imageData instanceof HTMLVideoElement
        ? imageData.videoWidth
        : imageData.width || imageData.clientWidth;
    sourceHeight =
      imageData instanceof HTMLVideoElement
        ? imageData.videoHeight
        : imageData.height || imageData.clientHeight;
    sourceImage = imageData;
  }

  // Expand bbox slightly for better face capture
  const expandFactor = 0.1;
  const expandedX = Math.max(0, bbox.x - bbox.w * expandFactor);
  const expandedY = Math.max(0, bbox.y - bbox.h * expandFactor);
  const expandedW = Math.min(sourceWidth - expandedX, bbox.w * (1 + 2 * expandFactor));
  const expandedH = Math.min(sourceHeight - expandedY, bbox.h * (1 + 2 * expandFactor));

  // Crop face region
  canvas.width = targetSize[0];
  canvas.height = targetSize[1];
  ctx.drawImage(
    sourceImage,
    expandedX,
    expandedY,
    expandedW,
    expandedH,
    0,
    0,
    targetSize[0],
    targetSize[1]
  );

  // Get pixel data and normalize to [-1, 1] range (common for face recognition models)
  const imageDataCropped = ctx.getImageData(0, 0, targetSize[0], targetSize[1]);
  const pixels = imageDataCropped.data;
  const normalized = new Float32Array(targetSize[0] * targetSize[1] * 3);

  // Normalize: (pixel / 255.0) * 2.0 - 1.0
  for (let i = 0; i < pixels.length; i += 4) {
    const idx = Math.floor(i / 4);
    normalized[idx * 3] = (pixels[i] / 255.0) * 2.0 - 1.0; // R
    normalized[idx * 3 + 1] = (pixels[i + 1] / 255.0) * 2.0 - 1.0; // G
    normalized[idx * 3 + 2] = (pixels[i + 2] / 255.0) * 2.0 - 1.0; // B
  }

  return {
    normalized,
    aligned: true, // TODO: implement proper face alignment using landmarks
  };
}

/**
 * Compute SHA-256 hash of frame data (deterministic)
 */
export async function computeFrameHash(
  landmarks: number[][],
  bbox: BBox,
  embedding: number[]
): Promise<string> {
  // Canonicalize: sort landmarks, round floats to fixed precision
  const canonicalLandmarks = landmarks
    .map(([x, y, z]) => [
      Math.round(x * 10000) / 10000,
      Math.round(y * 10000) / 10000,
      Math.round(z * 10000) / 10000,
    ])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2]);

  const canonicalBBox = {
    x: Math.round(bbox.x * 10000) / 10000,
    y: Math.round(bbox.y * 10000) / 10000,
    w: Math.round(bbox.w * 10000) / 10000,
    h: Math.round(bbox.h * 10000) / 10000,
  };

  const canonicalEmbedding = embedding.map((v) => Math.round(v * 100000) / 100000);

  const data = JSON.stringify({
    landmarks: canonicalLandmarks,
    bbox: canonicalBBox,
    embedding: canonicalEmbedding,
  });

  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return btoa(String.fromCharCode(...hashArray));
}

/**
 * Convert Float32Array to base64 string for JSON transmission
 */
export function float32ArrayToBase64(arr: Float32Array): string {
  const bytes = new Uint8Array(arr.buffer);
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Convert base64 string back to Float32Array
 */
export function base64ToFloat32Array(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Float32Array(bytes.buffer);
}
